import { useEffect, useMemo, useState } from 'react';
import { AlertCircle } from 'lucide-react';
import { Modal } from '@/components/ui/modal';
import { Button } from '@/components/ui/button';
import { Typeahead, type TypeaheadOption } from '@/components/ui/typeahead';
import { LoadingSpinner } from '@/components/ui/loading';
import { getDevices, updateDevice } from '@/features/devices/api/devices-api';
import { useDataCache } from '@/store/data-cache-context';
import type { Device, Unit } from '@/types/api';

interface AssignDeviceModalProps {
  isOpen: boolean;
  onClose: () => void;
  // The unit the picked device will be assigned to. When null the modal
  // never opens (the empty-state button only renders when a unit is
  // actually selected) but we accept null to keep the prop type flexible
  // for callers that hold the value as state.
  unit: Unit | null;
  onAssigned?: () => void;
}

// Modal that lets the operator pick from the org's currently-unassigned
// devices and bind one to a specific unit. Useful for unit-level empty
// states ("This unit has no devices — Add a new one or Assign an existing
// one"). Reads the unassigned set on open via getDevices() filtered
// client-side on `unitId`; refetches when the search term changes so big
// orgs don't have to load the full set up front.
export function AssignDeviceModal({ isOpen, onClose, unit, onAssigned }: AssignDeviceModalProps) {
  const { invalidate } = useDataCache();
  const [search, setSearch] = useState('');
  const [unassigned, setUnassigned] = useState<Device[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedDeviceId, setSelectedDeviceId] = useState<string>('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset state on open/close so a stale selection from a prior session
  // doesn't leak into a fresh assignment.
  useEffect(() => {
    if (!isOpen) {
      setSearch('');
      setSelectedDeviceId('');
      setError(null);
      setUnassigned([]);
      return;
    }
  }, [isOpen]);

  // Pull unassigned devices when the modal opens or the search changes.
  // The /devices endpoint doesn't have a server-side `unassigned=true`
  // filter today, so we fetch a page and filter client-side. perPage:50
  // is a balance: enough to scroll through, small enough to keep the
  // payload tight for searches with many matches.
  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    getDevices({ search: search.trim() || undefined, perPage: 50 })
      .then((res) => {
        if (cancelled) return;
        setUnassigned(res.items.filter((d) => !d.unitId));
      })
      .catch(() => {
        if (cancelled) return;
        setError('Could not load unassigned devices. Try again.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, search]);

  const options = useMemo<TypeaheadOption[]>(
    () =>
      unassigned.map((d) => ({
        value: d.id,
        label: d.serialNumber,
      })),
    [unassigned],
  );

  const selectedDevice = unassigned.find((d) => d.id === selectedDeviceId) ?? null;

  async function handleAssign() {
    if (!unit || !selectedDeviceId) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await updateDevice(selectedDeviceId, { unitId: unit.id });
      if (!res.success) {
        setError(res.message ?? 'Could not assign device.');
        return;
      }
      invalidate(['devices', 'units', 'properties']);
      onAssigned?.();
      onClose();
    } catch (err) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        (err instanceof Error ? err.message : 'Something went wrong — try again.');
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  }

  if (!unit) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`Assign device to Unit ${unit.unitNumber}`}
      description="Pick an unassigned device and bind it to this unit."
      size="md"
    >
      <div className="space-y-4">
        <Typeahead
          label="Device serial"
          required
          placeholder={loading ? 'Loading unassigned devices…' : 'Search by serial number…'}
          inputValue={search}
          onInputChange={(v) => {
            setSearch(v);
            setSelectedDeviceId('');
          }}
          onSelect={(opt) => {
            setSelectedDeviceId(opt.value);
            setSearch(opt.label);
          }}
          onCreateNew={() => {
            // No create-new flow from here — picker only.
          }}
          options={options}
          isLoading={loading}
          allowCreate={false}
        />

        {selectedDevice && (
          <div className="rounded-lg border border-primary-200 dark:border-primary-500/30 bg-primary-50 dark:bg-primary-500/10 px-3 py-2 text-sm">
            <p className="text-gray-600 dark:text-gray-400">Selected</p>
            <p className="font-mono font-semibold text-gray-900 dark:text-gray-100">
              {selectedDevice.serialNumber}
            </p>
          </div>
        )}

        {!loading && search.trim() && options.length === 0 && (
          <p className="text-xs text-gray-500 dark:text-gray-400">
            No unassigned devices match "{search.trim()}". Devices already on a unit can be moved
            from their device detail page.
          </p>
        )}

        {error && (
          <div className="flex items-start gap-2 rounded-lg border border-error-200 dark:border-error-500/30 bg-error-50 dark:bg-error-500/10 px-3 py-2 text-sm text-error-700 dark:text-error-400">
            <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}

        <div className="flex gap-3 pt-2">
          <Button variant="outline" onClick={onClose} className="flex-1" type="button">
            Cancel
          </Button>
          <Button
            onClick={handleAssign}
            disabled={!selectedDeviceId || submitting}
            isLoading={submitting}
            className="flex-1"
            type="button"
          >
            {submitting ? <LoadingSpinner size="sm" /> : 'Assign'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
